import { useEffect } from "react";
import {
  onlineManager,
  UseQueryOptions,
  useQuery,
  useQueryClient,
  QueryObserverOptions,
  QueryOptions,
} from "@tanstack/react-query";

import { merchantApiBaseUrl, swAppCombinedUrls } from "../../../constants";
import {
  ISwitchappAxiosClientConfig,
  useSwitchappAxiosClient,
} from "../useSwitchappAxiosClient";
import {
  ISwAppApi,
  ISwAppServiceApi,
  ISwAppServiceMetaApi,
  NoMeta,
} from "../../../interfaces-and-types";
import { appConfig, axiosBaseConfig } from "../../../configs";
import { useAuth } from "../../useAuth";

// type to enable dynamic query config
type TQueryObserverOption<TData> = UseQueryOptions<
  TData,
  unknown,
  TData,
  any
> & {};

export function useMerchantApiQuery<TResponseData, TMeta = NoMeta>(
  queryKey: keyof ISwAppApi,
  queryConfig: TQueryObserverOption<
    ISwAppServiceMetaApi<TResponseData, TMeta>
  > | null = null,
  getQueryUrl: (templateQueryUrl: string) => string = (val) => val,
  axiosConfig: ISwitchappAxiosClientConfig<null> | undefined = undefined
) {
  return useMerchantApiQueryWithParams<never, TResponseData, TMeta>(
    [queryKey, null],
    queryConfig,
    getQueryUrl,
    axiosConfig
  );
}

export function useMerchantApiQueryWithParams<
  TRequestData,
  TResponseData,
  TMeta = NoMeta
>(
  queryKey: [keyof ISwAppApi, TRequestData | null],
  queryConfig: TQueryObserverOption<
    ISwAppServiceMetaApi<TResponseData, TMeta>
  > | null = null,
  getQueryUrl: (templateQueryUrl: string) => string = (val) => val,
  axiosConfig: ISwitchappAxiosClientConfig<null> | undefined = undefined
) {
  const baseURL = axiosConfig?.baseURL ?? merchantApiBaseUrl;
  const { active_tenant_id } = useAuth();

  const axiosClient = useSwitchappAxiosClient(
    {
      baseURL,
      ...axiosBaseConfig,

      // ...axiosConfig
      hideApiErrorMessge: axiosConfig?.hideApiErrorMessge,
      hideResponseMessage: axiosConfig?.hideResponseMessage,
      hideAxiosErrorMessages: axiosConfig?.hideAxiosErrorMessages,
      ignoreActivityBlocker: axiosConfig?.ignoreActivityBlocker,
    },
    active_tenant_id ? active_tenant_id : undefined
  );

  // console.log(
  //   `running useMerchantApiQueryWithParams; tenantId: ${active_tenant_id}`
  // );

  // const useGetQueryTemplate = (id?: string) =>
  const useGetQuery = useQuery(
    queryKey,
    ({ queryKey: queryKeyAndParamsArray }) => {
      const [queryKey, queryParams] = queryKeyAndParamsArray;

      // NOTE: this will retrieve the url template from swAppCombinedUrls
      // and return it to the calling function to apply the necessary replacements
      const url = getQueryUrl(swAppCombinedUrls[queryKey]);

      return (
        axiosClient
          .get<
            TRequestData,
            { data: ISwAppServiceMetaApi<TResponseData, TMeta> }
          >(url, {
            params: queryParams, // useSwitchappAxiosClient.paramsSerializer() is used for serializing objects before passing to axios
          })
          // (url + `?${new URLSearchParams(queryParams).toString()}`) // serializing objects to dot notation before passing to axios
          .then((response) => response.data)
          .catch((error) => {
            throw error;
          })
      );
    },
    {
      // enabled: false, // for query testing only
      // refetchOnMount: false,
      retry: appConfig.queryRetryEnabled,
      refetchOnWindowFocus: false,

      refetchInterval: 1000 * 60 * appConfig.queryCacheIntervalInMins,
      cacheTime: 1000 * 60 * appConfig.queryCacheIntervalInMins,
      // select: (data) => data, // format data into desired shape
      ...queryConfig,
    }
  );

  // const useDownloadQuery = useQuery(
  //   queryKey,
  //   ({ queryKey: queryKeyAndParamsArray }) => {
  //     const [queryKey, queryParams] = queryKeyAndParamsArray;
  //     const url = swAppCombinedUrls[queryKey];

  //     console.log(
  //       `creating useGetQuery in useMerchantApiQueryWithParams;
  //       url: ${url}
  //       queryKey: ${queryKey}
  //       queryParams: ${queryParams}
  //       tenantId: ${tenantId}`
  //     );

  //     return (
  //       axiosClient
  //         .get<TRequestData, { data: TResponseData }>(url)
  //         // .post<TRequestData, { data: TResponseData }>(url, queryParams)
  //         .then((response) => {

  //       if (response.status === 200 || response.status === 201) {
  //         const headers = response.headers;
  //         const contentType = headers["content-type"];
  //         const contentDisposition = headers["content-disposition"];
  //         const fileDownloadName = contentDisposition
  //           .split(";")[1]
  //           .split("=")[1]
  //           .replace('"', "")
  //           .replace('"', "");

  //         const downloadUrl = window.URL.createObjectURL(
  //           new Blob([response.data], { type: contentType })
  //         );

  //         const link = document.createElement("a");

  //         link.href = downloadUrl;
  //         link.setAttribute("download", fileDownloadName); // any other extension
  //         document.body.appendChild(link);
  //         link.click();
  //         link.remove();
  //       }
  //         })
  //         .catch((error) => {
  //           throw error;
  //         })
  //     );
  //   },
  //   {
  //     // enabled: false, // for query testing only
  //     // refetchOnMount: false,
  //     refetchOnWindowFocus: false,
  //     refetchInterval: 1000 * 60 * appConfig.queryCacheIntervalInMins,
  //     // select: (data) => data, // format data into desired shape
  //     ...queryConfig,
  //   }
  // );

  // const useGetListQuery = useGetQueryTemplate();
  // const useGetByIdQuery = useGetQueryTemplate(putMutation);

  // return {
  //   useGetListQuery,
  //   useGetByIdQuery,
  // };

  return {
    ...useGetQuery,
    data: useGetQuery.data?.data,
    meta: useGetQuery.data?.meta,
    message: useGetQuery.data?.message,
    status: useGetQuery.data?.Status,
  };
}
